// =============================================================================
// src/features/analytics/analytics-view-controls.ts - Escopo, periodo e reacoes.
//
// 2026-09-02: os chips de Escopo (Nucleo · Omar · Contexto), o toggle "Mostrar
// reacoes" e os botoes de periodo (7/30 dias) deixam de filtrar o DOM — os
// buckets do feed sao LIMIT 12 no Hasura, filtrar local deixava 0-2 cards — e
// passam a trocar o RECORTE pedido ao servidor (analytics-view-state), que
// re-hidrata e remonta a rota. No mock o periodo segue o toast cosmetico.
// =============================================================================

import { qs, qsa } from '../../app/dom';
import { showMiniToast } from '../../chrome/mini-toast/index';
import { parseAnalyticsFeedScope } from '../../data/analytics-scope-where';
import { parseAnalyticsFeedSort } from '../../data/analytics-feed-sort';
import { OMAR_ANALYTICS_PERIOD_LABELS } from './analytics-data';
import { setAnalyticsPressedGroup, syncAnalyticsButtonsByValue } from './analytics-dom-sync';
import { analyticsLiveSource } from './analytics-live-store';
import {
  getAnalyticsViewState,
  parseAnalyticsPeriod,
  requestAnalyticsViewChange,
  type AnalyticsViewState,
} from './analytics-view-state';

export interface AnalyticsViewControlsDeps {
  /** O escopo age sobre o FEED: fora da view de mencoes o clique nao teria efeito visivel. */
  readonly ensureMentionsModule: () => void;
}

/** Liga periodo, chips de escopo e toggle de reacoes ao recorte do servidor. */
export function bindAnalyticsViewControls(deps: AnalyticsViewControlsDeps): void {
  qsa<HTMLButtonElement>('[data-analytics-period]').forEach(bindAnalyticsPeriodButton);
  qsa<HTMLButtonElement>('[data-analytics-relevance-filter]').forEach((scopeButton) => {
    bindAnalyticsScopeButton(scopeButton, deps);
  });
  qs<HTMLButtonElement>('[data-analytics-reactions-toggle]')?.addEventListener('click', () => {
    void submitAnalyticsViewChange({ showReactions: !getAnalyticsViewState().showReactions });
  });
  const sortSelect = qs<HTMLSelectElement>('[data-analytics-feed-sort]');
  if (sortSelect) bindAnalyticsFeedSortSelect(sortSelect);
}

/**
 * Ordenacao do feed (2026-09-20). Muda o RECORTE pedido ao servidor, nunca a
 * ordem dos cards ja renderizados: a lista e uma janela sobre o conjunto, e
 * reordenar o DOM so mexeria nos 25 itens da pagina aberta.
 */
function bindAnalyticsFeedSortSelect(sortSelect: HTMLSelectElement): void {
  sortSelect.addEventListener('change', () => {
    const sort = parseAnalyticsFeedSort(sortSelect.value);
    if (!sort) return;
    void submitAnalyticsViewChange({ sort }, 'Ordenar so existe no monitoramento ao vivo');
  });
}

/** Periodo REAL no live (janela de 7/30 dias no servidor); no mock so o toast. */
function bindAnalyticsPeriodButton(periodButton: HTMLButtonElement): void {
  periodButton.addEventListener('click', () => {
    setAnalyticsPressedGroup('[data-analytics-period]', periodButton);
    const period = parseAnalyticsPeriod(periodButton.dataset.analyticsPeriod);
    if (!period || analyticsLiveSource() !== 'live') {
      showMiniToast(`Periodo: ${analyticsPeriodLabelOf(periodButton)}`, 'ti-calendar-event');
      return;
    }
    void submitAnalyticsViewChange({ period });
  });
}

function bindAnalyticsScopeButton(scopeButton: HTMLButtonElement, deps: AnalyticsViewControlsDeps): void {
  scopeButton.addEventListener('click', () => {
    const scope = parseAnalyticsFeedScope(scopeButton.dataset.analyticsRelevanceFilter);
    if (!scope) return;
    syncAnalyticsButtonsByValue('data-analytics-relevance-filter', scope);
    deps.ensureMentionsModule();
    void submitAnalyticsViewChange({ scope });
  });
}

/** Troca o recorte no servidor; falha avisa e os controles voltam ao recorte anterior. */
async function submitAnalyticsViewChange(
  partial: Partial<AnalyticsViewState>,
  unavailableMessage = 'Recorte por escopo so existe no monitoramento ao vivo',
): Promise<void> {
  const outcome = await requestAnalyticsViewChange(partial);
  if (outcome === 'applied') return;
  syncAnalyticsViewControls(getAnalyticsViewState());
  showMiniToast(outcome === 'failed'
    ? 'Nao foi possivel carregar o recorte agora'
    : unavailableMessage, 'ti-alert-triangle');
}

/** Rail e periodo espelham o recorte em memoria (apos falha, sem re-render). */
export function syncAnalyticsViewControls(viewState: AnalyticsViewState): void {
  syncAnalyticsButtonsByValue('data-analytics-relevance-filter', viewState.scope);
  syncAnalyticsButtonsByValue('data-analytics-period', viewState.period);
  const sortSelect = qs<HTMLSelectElement>('[data-analytics-feed-sort]');
  if (sortSelect) sortSelect.value = viewState.sort;
  const reactionsToggle = qs<HTMLButtonElement>('[data-analytics-reactions-toggle]');
  reactionsToggle?.classList.toggle('is-active', viewState.showReactions);
  reactionsToggle?.setAttribute('aria-pressed', viewState.showReactions ? 'true' : 'false');
}

function analyticsPeriodLabelOf(periodButton: HTMLButtonElement): string {
  return periodButton.dataset.analyticsPeriod === 'last30'
    ? OMAR_ANALYTICS_PERIOD_LABELS.last30
    : OMAR_ANALYTICS_PERIOD_LABELS.last7;
}
